import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, setDoc, updateDoc, deleteDoc, serverTimestamp, query, orderBy, getDoc } from 'firebase/firestore';
import Swal from 'sweetalert2';
import AdminLayout from './AdminLayout';
import SetupGuard from '../components/SetupGuard';
import './AdminAccount.css';

const WorkzonesPage = () => {
  const params = useParams();
  const [locationId, setLocationId] = useState(params.locationId);
  const navigate = useNavigate();
  const auth = getAuth();
  
  const [user, setUser] = useState(null);
  const [workzones, setWorkzones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingWorkzone, setEditingWorkzone] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        if (!locationId) {
          const db = getFirestore();
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const selectedId = userDoc.data().selectedLocationId;
            if (selectedId) {
              setLocationId(selectedId);
            } else {
              Swal.fire('Info', 'Please select a location first', 'info');
              navigate('/admin/locations');
              return;
            }
          }
        }
        if (locationId) {
          await loadWorkzones(currentUser.uid);
        }
      } else {
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
  }, [locationId]);

  const loadWorkzones = async (uid) => {
    try {
      setLoading(true);
      const db = getFirestore();
      const snapshot = await getDocs(
        query(collection(db, 'users', uid, 'locations', locationId, 'workzones'), orderBy('order'))
      );
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setWorkzones(data);
    } catch (error) {
      console.error('Error loading workzones:', error);
      Swal.fire('Error', 'Failed to load workzones', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWorkzone = async (formValues) => {
    if (!user || !locationId) return;
    
    try {
      const db = getFirestore();
      if (editingWorkzone) {
        await updateDoc(doc(db, 'users', user.uid, 'locations', locationId, 'workzones', editingWorkzone.id), {
          name: formValues.name,
          order: formValues.order,
        });
      } else {
        await setDoc(doc(collection(db, 'users', user.uid, 'locations', locationId, 'workzones')), {
          name: formValues.name,
          order: formValues.order,
          createdAt: serverTimestamp(),
        });
      }
      
      setShowModal(false);
      setEditingWorkzone(null);
      await loadWorkzones(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
      Swal.fire('Success', `Workzone ${editingWorkzone ? 'updated' : 'created'}`, 'success');
    } catch (error) {
      console.error('Error saving workzone:', error);
      Swal.fire('Error', 'Failed to save workzone', 'error');
    }
  };

  const handleDeleteWorkzone = async (workzone) => {
    const result = await Swal.fire({
      title: 'Delete Workzone?',
      text: `Are you sure you want to delete "${workzone.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
    });

    if (result.isConfirmed && user) {
      try {
        const db = getFirestore();
        await deleteDoc(doc(db, 'users', user.uid, 'locations', locationId, 'workzones', workzone.id));
        await loadWorkzones(user.uid);
        window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
        Swal.fire('Deleted', 'Workzone deleted', 'success');
      } catch (error) {
        console.error('Error deleting workzone:', error);
        Swal.fire('Error', 'Failed to delete workzone', 'error');
      }
    }
  };

  const openAddModal = () => {
    setEditingWorkzone(null);
    setShowModal(true);
  };

  const openEditModal = (workzone) => {
    setEditingWorkzone(workzone);
    setShowModal(true);
  };

  if (loading) {
    return (
      <AdminLayout title="Workzones">
        <div className="text-center py-5">
          <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Workzones">
      <SetupGuard prerequisites={['location']}>
      <div className="admin-account-page">
        {/* Add Workzone Button */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="m-0">Manage Workzones</h5>
              <button className="btn btn-primary" onClick={openAddModal}>
                <i className="fas fa-plus mr-2"></i>Add Workzone
              </button>
            </div>
          </div>
        </div>

        {/* Workzone List */}
        <div className="row">
          {workzones.length === 0 ? (
            <div className="col-12 text-center py-5">
              <i className="fas fa-building fa-4x text-muted mb-3"></i>
              <h5 className="text-muted">No workzones yet</h5>
              <p className="text-muted">Click "Add Workzone" to create your first workzone</p>
            </div>
          ) : (
            workzones.map(workzone => (
              <div key={workzone.id} className="col-md-6 col-lg-4 mb-3">
                <div className="card">
                  <div className="card-body d-flex align-items-center">
                    <div
                      className="d-flex align-items-center justify-content-center mr-3"
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 10,
                        background: 'rgba(40,167,69,0.1)',
                        color: '#28a745',
                      }}
                    >
                      <i className="fas fa-building"></i>
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="m-0 font-weight-bold">{workzone.name}</h6>
                      <small className="text-muted">Order: {workzone.order}</small>
                    </div>
                    <div className="position-relative">
                      <button 
                        className="btn btn-sm btn-link" 
                        onClick={() => setOpenDropdownId(openDropdownId === workzone.id ? null : workzone.id)}
                      >
                        <i className="fas fa-ellipsis-v"></i>
                      </button>
                      {openDropdownId === workzone.id && (
                        <div 
                          className="card position-absolute" 
                          style={{ 
                            right: 0, 
                            top: '100%', 
                            zIndex: 1000, 
                            minWidth: '120px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                          }}
                        >
                          <div className="list-group list-group-flush">
                            <button 
                              className="list-group-item list-group-item-action py-2" 
                              onClick={() => { setOpenDropdownId(null); openEditModal(workzone); }}
                            >
                              <i className="fas fa-edit mr-2 text-primary"></i>Edit
                            </button>
                            <button 
                              className="list-group-item list-group-item-action py-2 text-danger" 
                              onClick={() => { setOpenDropdownId(null); handleDeleteWorkzone(workzone); }}
                            >
                              <i className="fas fa-trash mr-2"></i>Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Workzone Modal */}
        {showModal && (
          <WorkzoneFormModal
            workzone={editingWorkzone}
            workzones={workzones}
            onSave={handleSaveWorkzone}
            onClose={() => { setShowModal(false); setEditingWorkzone(null); }}
          />
        )}
      </div>
      </SetupGuard>
    </AdminLayout>
  );
};

const WorkzoneFormModal = ({ workzone, workzones, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: workzone?.name || '',
    order: workzone?.order ?? workzones.length,
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Please enter a workzone name';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title font-weight-bold">{workzone ? 'Edit Workzone' : 'Add Workzone'}</h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="form-group">
                <label>Workzone Name *</label>
                <input
                  type="text"
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
              </div>
              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  className="form-control"
                  style={{ width: 100 }}
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}>
                {workzone ? 'Update' : 'Create'} Workzone
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WorkzonesPage;
